/** @jsxImportSource @emotion/react */

import React, { useState } from 'react'
import { KeyboardArrowDown } from '@mui/icons-material'

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { UnitsTable } from './units-table'
import { PropertyResponseItem } from 'components/search/models/property-search-response'
import { PropertyHeader, propertyStyles } from 'components/property/property-helper'
import {
    formatCadastreNumber,
    getAllDistinctPropertyBuildingNumbers,
    getMainPropertyCadastreNumber,
    isMainProperty,
    isPropertySectioned2,
} from 'utils/cadastre-utils'
import { InePropertyProduct } from 'components/property/InePropertyProduct'

const showDetachedHousingUnits = (units: PropertyResponseItem[]) => {
    const main = units.find((u) => isMainProperty(u.cadastreNumber))
    if (main && main.housingUnits.length > 0 && isPropertySectioned2(units)) {
        return true
    }
    return false
}

const getDetachedHousingUnits = (units: PropertyResponseItem[]): PropertyResponseItem[] => {
    return units
        .filter((u) => isMainProperty(u.cadastreNumber))
        .map((u) =>
            u.housingUnits.map((hu) => {
                return {
                    cadastreNumber: u.cadastreNumber,
                    housingUnits: [hu],
                    owners: hu.owners,
                }
            }),
        )
        .flat()
}

type Props = {
    units: PropertyResponseItem[]
}

export const OsloUserUnitsList: React.FC<Props> = ({ units }) => {
    const [opened, setOpened] = useState<number[]>([])
    const { t } = useTranslation()
    const styles = propertyStyles
    const [searchParams] = useSearchParams()
    const nr = searchParams.get('cadastreNumber') || ''

    const cadastreNumber = getMainPropertyCadastreNumber(nr)
    const isSectioned = isPropertySectioned2(units)
    const allBuildingNumbers = getAllDistinctPropertyBuildingNumbers(units)

    const handleOpened = (cadastreId: number) => {
        if (opened.includes(cadastreId)) {
            setOpened(opened.filter((o) => o !== cadastreId))
        } else {
            setOpened([...opened, cadastreId])
        }
    }

    return (
        <div>
            {isSectioned && isMainProperty(cadastreNumber) && (
                <div css={styles.sectionTitle}>{t('MAIN_PROPERTY', 'Grunneiendom')}</div>
            )}

            {!isSectioned && (
                <InePropertyProduct cadastreNumber={cadastreNumber} propertyBuildingNumbers={allBuildingNumbers} />
            )}
            <div tabIndex={0}>
                {isSectioned && isMainProperty(cadastreNumber) && (
                    <>
                        <PropertyHeader isMain={true} />
                        <div css={styles.propertyLine} onClick={() => handleOpened(0)}>
                            <div css={[styles.column1, styles.gridItem]}>{formatCadastreNumber(cadastreNumber)}</div>
                            <div css={[styles.column2, styles.gridItem]} />
                            <div css={[styles.column3, styles.gridItem]} />
                            <div css={[styles.column4, styles.gridItem]} />
                            <div css={[styles.column5, styles.gridItem]} />
                            <div css={[styles.column6, styles.gridItem]} />
                            <div css={[styles.column7, styles.gridItem]}>
                                <KeyboardArrowDown />
                            </div>
                        </div>
                        <br />
                        {opened.includes(0) && (
                            <div css={styles.expandedContent}>
                                <InePropertyProduct
                                    cadastreNumber={cadastreNumber}
                                    propertyBuildingNumbers={allBuildingNumbers}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {isSectioned && (
                <UnitsTable
                    units={units.filter((u) => !isMainProperty(u.cadastreNumber))}
                    title={t('SECTIONS', 'Seksjoner ({{huCount}})', {
                        huCount: units.filter((u) => !isMainProperty(u.cadastreNumber)).length,
                    })}
                />
            )}
            {showDetachedHousingUnits(units) && (
                <UnitsTable
                    units={getDetachedHousingUnits(units)}
                    title={t('DETACHED_HUS', 'Bruksenheter som ikke er tilknyttet seksjon i matrikkel ({{huCount}})', {
                        huCount: getDetachedHousingUnits(units).length,
                    })}
                />
            )}
            {!showDetachedHousingUnits(units) && getDetachedHousingUnits(units).length > 0 && (
                <UnitsTable
                    units={getDetachedHousingUnits(units)}
                    title={t('HOUSING_UNITS', 'Bruksenheter ({{huCount}})', {
                        huCount: getDetachedHousingUnits(units).length,
                    })}
                />
            )}
        </div>
    )
}
